/**
 * Config — 6-tab migration of the legacy Angular `/config` page.
 *
 * Status:
 *   ✅ Communication  — email validation, IMAP + Validate, Mailgun, reminders,
 *                       external insurance
 *   ✅ Applicant      — refund & lead, credit report + Equifax XML/JSON, Plaid,
 *                       Ninja V1/V2
 *   ✅ Payments       — 4-gateway matrix, CardConnect creds, default ACH,
 *                       UpTempo program, payment window
 *   ✅ Security       — captcha, session timeout, OTP content + backup,
 *                       notification banner
 *   ✅ Mobile App     — iOS/Android versions, maintenance banner
 *   ✅ Yardi          — full Yardi creds with 1–365 day fetch-range guard
 *
 * The hidden `salesteam` tab (d-none in Angular) is intentionally omitted.
 *
 * Data fetch: one GET /api/config on mount; per-tab extractors slice the
 * shared response. Saves go back via POST /api/config — each tab only
 * sends its own keys so other tabs' values on the backend are preserved.
 */

import { useEffect, useState } from 'react'
import { EntityEditTabBar, type EntityEditTabItem } from '@/components/ui/EntityEditTabBar'
import { PageShell } from '@/components/layout/PageShell'
import { showApiError } from '@/lib/utils/notifications'
import { ApplicantTab } from '../components/tabs/ApplicantTab'
import { CommunicationTab } from '../components/tabs/CommunicationTab'
import { MobileAppTab } from '../components/tabs/MobileAppTab'
import { PaymentsTab } from '../components/tabs/PaymentsTab'
import { SecurityTab } from '../components/tabs/SecurityTab'
import { YardiTab } from '../components/tabs/YardiTab'
import {
  extractApplicantValues,
  extractCommunicationValues,
  extractMobileAppValues,
  extractMrgRows,
  extractPaymentValues,
  extractSecurityValues,
  extractYardiValues,
  getConfig,
  type MrgRow,
} from '../services/configService'
import type {
  ApplicantFormValues,
  CommunicationFormValues,
  ConfigApiResponse,
  MobileAppFormValues,
  PaymentFormValues,
  SecurityFormValues,
  YardiFormValues,
  YardiPlaceholders,
} from '../types/config'
import type { ConfigLegacyTabId } from '../types'

const TABS: { id: ConfigLegacyTabId; label: string }[] = [
  { id: 'communication', label: 'Communication' },
  { id: 'applicant', label: 'Applicant Screening' },
  { id: 'payment', label: 'Payments' },
  { id: 'security', label: 'Security' },
  { id: 'mobileapp', label: 'Mobile Application' },
  { id: 'yardicreds', label: 'Yardi' },
]

const CONFIG_TAB_ITEMS: EntityEditTabItem[] = TABS.map((tab) => ({
  id: tab.id,
  label: tab.label,
  testId: `config-tab-${tab.id}`,
}))

export function ConfigLegacyPage() {
  const [selectedTab, setSelectedTab] = useState<ConfigLegacyTabId>('communication')
  const [loading, setLoading] = useState(true)

  const [communication, setCommunication] = useState<CommunicationFormValues | null>(null)
  const [applicant, setApplicant] = useState<ApplicantFormValues | null>(null)
  const [payment, setPayment] = useState<PaymentFormValues | null>(null)
  const [security, setSecurity] = useState<SecurityFormValues | null>(null)
  const [mobileApp, setMobileApp] = useState<MobileAppFormValues | null>(null)
  const [yardi, setYardi] = useState<YardiFormValues | null>(null)
  const [yardiPlaceholders, setYardiPlaceholders] = useState<YardiPlaceholders>({
    interfaceEntity: '',
    interfaceLicense: '',
  })
  const [mrgRows, setMrgRows] = useState<MrgRow[]>([])

  const load = async () => {
    setLoading(true)
    try {
      const raw: ConfigApiResponse = await getConfig()
      setCommunication(extractCommunicationValues(raw))
      setApplicant(extractApplicantValues(raw))
      setPayment(extractPaymentValues(raw))
      setMrgRows(extractMrgRows(raw))
      setSecurity(extractSecurityValues(raw))
      setMobileApp(extractMobileAppValues(raw))
      const y = extractYardiValues(raw)
      setYardi(y.values)
      setYardiPlaceholders(y.placeholders)
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  return (
    <PageShell title="CONFIG">
      <div className="bg-white shadow-sm border border-slate-200 overflow-visible rounded-lg ring-1 ring-slate-100">
        <EntityEditTabBar
          navTestId="config-tab-nav"
          ariaLabel="Config sections"
          tabs={CONFIG_TAB_ITEMS}
          activeTab={selectedTab}
          onSelect={(id) => setSelectedTab(id as ConfigLegacyTabId)}
        />

        <div
          role="tabpanel"
          className="p-4 sm:p-6 bg-slate-50/50 min-h-[300px] sm:min-h-[400px]"
        >
          {selectedTab === 'communication' && (
            <CommunicationTab
              initialValues={communication}
              isLoading={loading}
              onSaved={() => void load()}
            />
          )}
          {selectedTab === 'applicant' && (
            <ApplicantTab
              initialValues={applicant}
              isLoading={loading}
              onSaved={() => void load()}
            />
          )}
          {selectedTab === 'payment' && (
            <PaymentsTab
              initialValues={payment}
              mrgRows={mrgRows}
              isLoading={loading}
              yardiDataFetchRangeByDays={yardi?.YARDI_DATA_FETCH_RANGE_BY_DAYS ?? ''}
              onSaved={() => void load()}
            />
          )}
          {selectedTab === 'security' && (
            <SecurityTab
              initialValues={security}
              isLoading={loading}
              onSaved={() => void load()}
            />
          )}
          {selectedTab === 'mobileapp' && (
            <MobileAppTab
              initialValues={mobileApp}
              isLoading={loading}
              onSaved={() => void load()}
            />
          )}
          {selectedTab === 'yardicreds' && (
            <YardiTab
              initialValues={yardi}
              placeholders={yardiPlaceholders}
              isLoading={loading}
              onSaved={() => void load()}
            />
          )}
        </div>
      </div>
    </PageShell>
  )
}
